import { useMutation, useQueryClient } from 'react-query';

import { CreateProceduralEventInput } from './useCreateProceduralEvent';
import type { ProceduralTimelineEvent } from '../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { parseErrorMessage } from '@/api/parse-error';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

export interface UpdateProceduralEventInput extends Partial<CreateProceduralEventInput> {
  /** Generate recalculated Update suggestions for dependent events (opt-in). */
  recalculateDependents?: boolean;
}

interface UpdateProceduralEventParams {
  matterId: string;
  eventId: string;
  input: UpdateProceduralEventInput;
}

export const useUpdateProceduralEvent = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<ProceduralTimelineEvent, Error, UpdateProceduralEventParams>(
    async ({ matterId, eventId, input }) => {
      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/events/${eventId}`,
        getFetchConfig({ method: FetchMethod.PATCH, data: input }),
      );
      if (!res.ok) {
        throw new Error((await parseErrorMessage(res)) || 'Failed to update event');
      }
      return res.json();
    },
    {
      onSuccess: (_, { matterId, eventId, input }) => {
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineEvents, { matterId }]);
        // The cascade may have created suggestions for dependent events, which
        // also gates upload eligibility (a matter with pending suggestions can't
        // start a new extraction).
        if (input.recalculateDependents === true) {
          queryClient.invalidateQueries([QueryEntity.ProceduralTimelineSuggestions, { matterId }]);
          queryClient.invalidateQueries([QueryEntity.ProceduralTimelineUploadEligibility, { matterId }]);
        }
        trackCustomEvent(TrackingEventsV2.ProceduralTimelineEventUpdate, {
          matterId,
          eventId,
          fieldsChanged: Object.keys(input),
        });
      },
    },
  );
};
